import api from '../../common/api/api';
import type { ConversationFilter, SortOrder } from '../components/Kanban/interfaces/kanban';

// Ola 2: 'true' reads lead statuses and the kanban columns from main_service REST. Writes stay on GraphQL.
export const isLeadStatusesRest = (): boolean => process.env.NEXT_PUBLIC_REST_LEAD_STATUSES === 'true';

type Row = Record<string, any>;
type Page = { items: any[]; total?: number; has_more: boolean; next_cursor?: string | null };
export type BoardColumnPage = Page & { status_id: string };

// MAX_BOT_IDS of GET /organizations/{id}/lead-statuses.
const ORG_BOT_IDS_CHUNK = 200;

async function getPage(path: string, params?: URLSearchParams): Promise<Page> {
  const res = await api.get(path, params);
  if (!res.success || !res.data) throw new Error(`${path} ${res.status ?? ''}`.trim());
  return res.data as Page;
}

// Ids are encoded so a value from the URL can't walk to another API path.
const bot = (botId: string, sub = '') => `bots/${encodeURIComponent(botId)}${sub}`;

export const fetchBotLeadStatusesRest = async (botId: string): Promise<Row[]> => (
  (await getPage(bot(botId, '/lead-statuses'))).items
);

// bot_ids repeated (FastAPI), not axios' bot_ids[]=.
export async function fetchOrgLeadStatusesRest(orgId: string, botIds: string[]): Promise<Row[]> {
  const chunks: URLSearchParams[] = [];
  for (let i = 0; i < botIds.length; i += ORG_BOT_IDS_CHUNK) {
    chunks.push(new URLSearchParams(botIds.slice(i, i + ORG_BOT_IDS_CHUNK).map((id) => ['bot_ids', id])));
  }
  const path = `organizations/${encodeURIComponent(orgId)}/lead-statuses`;
  const pages = await Promise.all(chunks.map((params) => getPage(path, params)));
  return pages.flatMap((p) => p.items).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

export type BoardFilters = {
  selectedTags?: string[];
  conversationFilter?: ConversationFilter;
  searchFilter?: string;
  selectedLeadStatuses?: string[];
  assignedTo?: string[];
  countries?: string[];
  sortOrder?: SortOrder;
  fromDate?: Date | null;
};

const realIds = (ids?: string[]) => (ids?.length && !ids.includes('all') ? ids : []);

// Same filters as useBoardState.buildContactFilters; sales are scoped to their own sessions server side.
export const buildBoardParams = (f: BoardFilters, limit: number, offset: number, idsOnly = false) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  params.set(idsOnly ? 'ids_only' : 'include_count', 'true');
  (f.selectedTags ?? []).forEach((tag) => params.append('tags', tag));
  // The contacts page also passes 'all' (no filter); the API only takes with/without.
  const conv = f.conversationFilter as string | undefined;
  if (conv === 'with' || conv === 'without') params.set('conversation', conv);
  if (f.searchFilter?.trim()) params.set('q', f.searchFilter.trim());
  realIds(f.selectedLeadStatuses).forEach((id) => params.append('lead_status_ids', id));
  realIds(f.assignedTo).forEach((id) => params.append('assigned_to', id));
  (f.countries ?? []).forEach((c) => params.append('countries', c));
  params.set('sort', f.sortOrder === 'oldest' || f.sortOrder === 'fromDate' ? 'oldest' : 'newest');
  const from = f.fromDate;
  if (f.sortOrder === 'fromDate' && from instanceof Date && !Number.isNaN(from.getTime())) {
    params.set('last_message_from', from.toISOString());
  }
  return params;
};

export const fetchBoardColumnRest = (botId: string, statusId: string, params: URLSearchParams) => (
  getPage(bot(botId, `/lead-statuses/${encodeURIComponent(statusId)}/contacts`), params)
);

// Every column's first page in one call; params as buildBoardParams plus per_column.
export async function fetchBoardRest(botId: string, params: URLSearchParams): Promise<BoardColumnPage[]> {
  const res = await api.get(bot(botId, '/board'), params);
  if (!res.success || !res.data) throw new Error(`board ${res.status ?? ''}`.trim());
  return res.data.columns as BoardColumnPage[];
}
